<template>
    <generic-task-execution-modal
        :asset="asset"
        :curie-id="curieId"
        :assignable-users="assignableUsers"
        :description="description"
        :task-resolved="taskResolved"
        :primary-button-blocked="photoUploadInProgress"
        :form="form"
        :performed-by-mode="performedByMode"
        :presence-from-start-here-scan="presenceFromStartHereScan"
        :requires-presence-detection="requiresPresenceDetection"
        :editable="editable"
        :task-code="taskCode"
        :task-group="taskGroup"
        :task-id="taskId"
        :title="title"
        @submit="handleSubmit"
    >
        <div class="my-3 flex flex-col gap-20">
            <div v-for="group in groupedSteps" :key="group.id">
                <h4 v-if="group.label" class="!text-lg !font-bold" @click.stop="toggleSteps(group.id)">{{ group.label }}</h4>

                <div class="flex flex-col gap-2">
                    <template v-for="step in group.steps" :key="step.id">
                        <safety-inspection-step
                            v-if="step.type === 'safety_inspection'"
                            :deficiencies="step.deficiencies ?? []"
                            :disabled="!editable"
                            :label="step.label"
                            :result="getStepById(step.id)?.result"
                            :selected-deficiencies="getStepById(step.id)?.metadata?.deficiencies ?? []"
                            @change="updateFormStep(step.id, $event)"
                            @update:deficiencies="updateFormStepDeficiencies(step.id, $event)"
                        />
                        <testing-sequence-photo-step
                            v-else-if="step.type === 'photo'"
                            :disabled="!editable"
                            :existing-photo-url="existingPhotos?.[step.id]?.uri ?? null"
                            :hint="step.hint ?? null"
                            :label="step.label"
                            :value="getStepById(step.id)?.result ?? null"
                            @change="updateFormStep(step.id, $event)"
                            @upload-started="photoUploadInProgress = true"
                            @upload-completed="photoUploadInProgress = false"
                        />
                        <testing-sequence-time-step
                            v-else-if="step.type === 'time'"
                            :disabled="!editable"
                            :label="step.label"
                            :value="getStepById(step.id)?.result ?? null"
                            @change="updateFormStep(step.id, $event)"
                        />
                        <testing-sequence-location-step
                            v-else-if="step.type === 'location'"
                            :disabled="!editable"
                            :location-tree="locationTree"
                            :value="getStepById(step.id)?.result ?? null"
                            @change="updateFormStep(step.id, $event)"
                        />
                        <testing-sequence-number-step
                            v-else-if="step.type === 'number'"
                            :disabled="!editable"
                            :label="step.label"
                            :value="getStepById(step.id)?.result ?? null"
                            @change="updateFormStep(step.id, $event)"
                        />
                        <testing-sequence-confirmation-step
                            v-else-if="step.type === 'boolean_confirmation'"
                            :step-id="step.id"
                            :label="step.label"
                            :disabled="!editable"
                            :result="getStepById(step.id)?.result ?? null"
                            @change="updateFormStep(step.id, $event)"
                        />
                        <testing-sequence-step
                            v-else
                            :checked="isChecked(step)"
                            :disabled="!editable"
                            :label="step.label"
                            @change="updateFormStep(step.id, $event)"
                        />
                    </template>
                </div>
            </div>
        </div>
        <div v-if="stepsHaveErrors" class="invalid-feedback !block px-2 py-1">{{ trans('tasks.steps.validation.completed') }}</div>
    </generic-task-execution-modal>
</template>
<script setup>
import { computed, defineAsyncComponent, ref } from 'vue';
import { useForm } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n';
import TestingSequenceStep from '@/Components/TestingSequenceStep.vue';
import TestingSequenceNumberStep from '@/Components/TestingSequenceNumberStep.vue';
import TestingSequencePhotoStep from '@/Components/TestingSequencePhotoStep.vue';
import TestingSequenceTimeStep from '@/Components/TestingSequenceTimeStep.vue';
import TestingSequenceLocationStep from '@/Components/TestingSequenceLocationStep.vue';
import TestingSequenceConfirmationStep from '@/Components/TestingSequenceConfirmationStep.vue';
import SafetyInspectionStep from '@/Components/SafetyInspectionStep.vue';
const GenericTaskExecutionModal = defineAsyncComponent(() => import('@/Pages/Modals/GenericTaskExecutionModal.vue'));

const props = defineProps({
    asset: {
        type: Object,
        required: true,
    },
    presenceFromStartHereScan: {
        type: Boolean,
        default: false,
    },
    requiresPresenceDetection: {
        type: Boolean,
        default: false,
    },
    curieId: {
        type: String,
        default: null,
    },
    title: String,
    description: String,
    taskId: String,
    taskCode: String,
    taskGroup: String,
    taskType: String,
    assetId: String,
    editable: {
        type: Boolean,
        default: false,
    },
    assignableUsers: Object,
    currentUserId: String,
    performedByMode: { type: String, default: 'select' },
    links: Object,
    steps: Array,
    locationTree: { type: Object, default: null },
    existingSteps: { type: Array, default: () => [] },
    existingDescription: String,
    existingPerformedById: String,
    existingPhotos: { type: Object, default: () => ({}) },
    taskResolved: { type: Boolean, default: false },
});

const emit = defineEmits(['created']);

const photoUploadInProgress = ref(false);

const existingStepsById = (props.existingSteps || []).reduce((acc, step) => {
    acc[step.id] = step;
    return acc;
}, {});

const form = useForm({
    description: props.existingDescription ?? null,
    attachments: [],
    performedById: props.existingPerformedById ?? props.currentUserId,
    taskId: props.taskId,
    task_type: props.taskType,
    asset_id: props.assetId,
    steps: props.steps.reduce((acc, step) => {
        // Unify grouped and ungrouped steps
        const stepsInGroup = step.steps || [step];

        stepsInGroup.forEach((innerStep) => {
            const existing = existingStepsById[innerStep.id];
            const fallback = ['number', 'photo', 'time', 'safety_inspection', 'location'].includes(innerStep.type) ? null : undefined;
            const existingDeficiencies = existing?.metadata?.deficiencies ?? [];
            acc.push({
                id: innerStep.id,
                result: existing ? existing.result : fallback,
                ...(innerStep.type ? { type: innerStep.type } : {}),
                ...(innerStep.type === 'safety_inspection' ? { metadata: { deficiencies: existingDeficiencies } } : {}),
            });
        });

        return acc;
    }, []),
});

// Index steps for constant-time lookups by id
const stepIndexById = form.steps.reduce((acc, step, index) => {
    acc[step.id] = index;
    return acc;
}, {});

const getStepById = (stepId) => {
    const index = stepIndexById[stepId];
    return typeof index === 'number' ? form.steps[index] : undefined;
};

const isChecked = (step) => getStepById(step.id)?.result;

const groupedSteps = computed(() => {
    return props.steps.reduce((acc, step) => {
        const groupId = step.group ? step.id : 'default';

        if (!acc[groupId]) {
            acc[groupId] = {
                id: groupId,
                label: step.group,
                steps: [],
            };
        }

        if (Array.isArray(step.steps)) {
            acc[groupId].steps.push(...step.steps);
        } else {
            acc[groupId].steps.push(step);
        }
        return acc;
    }, {});
});

const showTitle = computed(() => props.steps.find((step) => step.group) === undefined);
const stepsHaveErrors = computed(() => Object.keys(form.errors).some((key) => key.includes('step')));

const toggleSteps = (groupId) => {
    // Get step ids for the group
    const stepIds = groupedSteps.value[groupId].steps.map((step) => step.id);

    // Resolve steps via index for performance
    const groupSteps = stepIds.map((id) => getStepById(id)).filter(Boolean);

    const allCompleted = groupSteps.every((step) => !!step.result);

    groupSteps.forEach((step) => {
        step.result = !allCompleted;
    });
};

const updateFormStep = (stepId, value) => {
    const step = getStepById(stepId);
    if (!step) return;

    step.result = value;

    // Confirming safety inspection clears any previously picked deficiencies.
    if (step.type === 'safety_inspection' && value !== false) {
        step.metadata = { deficiencies: [] };
    }
};

const updateFormStepDeficiencies = (stepId, deficiencies) => {
    const step = getStepById(stepId);
    if (!step) return;

    step.metadata = { deficiencies };
};

const handleSubmit = (modal) => {
    form.transform(function (data) {
        data.steps = data.steps.map((step) => ({
            id: step.id,
            result: step.result,
            ...(step.type ? { type: step.type } : {}),
            ...(step.type === 'safety_inspection' ? { metadata: step.metadata ?? { deficiencies: [] } } : {}),
        }));

        return data;
    }).post(props.links.create, {
        preserveScroll: true,
        onSuccess: () => {
            modal.emit('created', {
                assetId: props.asset.id,
                ...form.data(),
            });

            modal.close();
        },
    });
};
</script>
